import { useMemo } from "react";
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
} from "@tanstack/react-table";

type ResultRow = Record<string, unknown>;

export interface ResultsTableProps {
  resultRows: ResultRow[];
  outputHeaders: string[];
}

const RESULTS_ROW_LIMIT = 200;

export function ResultsTable({ resultRows, outputHeaders }: ResultsTableProps) {
  const columns = useMemo<ColumnDef<ResultRow>[]>(
    () =>
      outputHeaders.map((key) => ({
        id: key,
        accessorFn: (row) => row[key],
        header: key,
        cell: ({ getValue }) => String(getValue() ?? ""),
      })),
    [outputHeaders]
  );

  const data = useMemo(
    () => resultRows.slice(0, RESULTS_ROW_LIMIT),
    [resultRows]
  );

  // TanStack Table returns non-memoizable instance methods by design; React
  // Compiler skips this hook automatically.
  // eslint-disable-next-line react-hooks/incompatible-library
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="overflow-auto max-h-[70vh]">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-surface-800">
          {table.getHeaderGroups().map((hg) => (
            <tr key={hg.id}>
              {hg.headers.map((header) => (
                <th
                  key={header.id}
                  className="text-left px-3 py-2 border-b border-slate-700 font-medium"
                >
                  {flexRender(
                    header.column.columnDef.header,
                    header.getContext()
                  )}
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody>
          {table.getRowModel().rows.map((row) => (
            <tr key={row.id} className="odd:bg-surface-800/40">
              {row.getVisibleCells().map((cell) => (
                <td
                  key={cell.id}
                  className="px-3 py-2 border-b border-slate-800 align-top whitespace-pre-wrap"
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {resultRows.length > RESULTS_ROW_LIMIT && (
        <p className="text-xs text-slate-500 mt-2">
          Showing first {RESULTS_ROW_LIMIT} rows…
        </p>
      )}
    </div>
  );
}
